import { api } from '@goaltree/backend/convex/_generated/api'
import { useQuery } from 'convex/react'
import { useSyncExternalStore } from 'react'

import { useUpdatePreferences } from '@/hooks/use-update-preferences'

export type HabitCheckStyle = 'tap' | 'hold'

const KEY = 'goaltree-habit-check-style'
const listeners = new Set<() => void>()

function readLocal(): HabitCheckStyle {
  try {
    return localStorage.getItem(KEY) === 'hold' ? 'hold' : 'tap'
  } catch {
    return 'tap'
  }
}

function writeLocal(style: HabitCheckStyle) {
  try {
    localStorage.setItem(KEY, style)
  } catch {}
  listeners.forEach((l) => l())
}

const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => void listeners.delete(cb)
}

// Synced via userPreferences.habitCheckStyle; the local copy covers devices
// (and deployments) where the server value isn't set yet.
export function useHabitCheckStyle(): HabitCheckStyle {
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { habitCheckStyle?: HabitCheckStyle }
    | undefined
  const local = useSyncExternalStore(subscribe, readLocal, () => 'tap' as const)
  return prefs?.habitCheckStyle ?? local
}

export function useSetHabitCheckStyle() {
  const update = useUpdatePreferences()
  return (style: HabitCheckStyle) => {
    writeLocal(style)
    void update({ habitCheckStyle: style }).catch(() => {})
  }
}
